import type { Contact, ContactResponse } from '@common/types/contacts';
import type { VerificationRequestResponse } from '@common/types/treefort';

export type IDVerificationClient = {
    id: string;
    contactDetailId?: string;
    name: string;
    email: string;
    phone: string;
    category: string;
    countryCode?: string;
    dialCode?: number;
};

function normalizePhoneNumber(phoneNumber?: string | null): string {
    return (phoneNumber ?? '').replace(/\D/g, '');
}

function normalizeEmail(email?: string | null): string {
    return (email ?? '').trim().toLowerCase();
}

function formatContactName(
    firstName?: string | null,
    middleName?: string | null,
    lastName?: string | null,
): string {
    return [firstName, middleName, lastName].filter(Boolean).join(' ').trim();
}

export function mapContactResponsesToIDVClients(
    contacts?: Contact[] | null,
): IDVerificationClient[] {
    return (contacts ?? [])
        .map((contact) => ({
            id: contact.contactId,
            contactDetailId: contact.contactId,
            name: formatContactName(contact.firstName, contact.middleName, contact.lastName),
            email: contact.contactEmail ?? '',
            phone: normalizePhoneNumber(contact.phoneNumber),
            category: 'Client',
        }))
        .filter((contact) => contact.name || contact.email);
}

export function buildContactNameMap(
    contacts: ContactResponse[],
    contactKey: 'contactDetailId' | 'contactId',
): Record<string, string> {
    return contacts.reduce(
        (map, contact) => {
            map[contact[contactKey]] = formatContactName(
                contact.firstName,
                contact.middleName,
                contact.lastName,
            );
            return map;
        },
        {} as Record<string, string>,
    );
}

const BLOCKED_IDV_STATUSES = new Set(['Request Pending', 'Review Report', 'Cancelled']);

export function isIdVerificationClientDisabled(
    contactDetailId: string | undefined,
    latestStatuses: Record<string, { requestStatus: string; requesterClientRequestId: string }>,
): boolean {
    const clientStatus = Object.values(latestStatuses).find(
        (status) => status.requesterClientRequestId === contactDetailId,
    );

    return !!clientStatus && BLOCKED_IDV_STATUSES.has(clientStatus.requestStatus);
}

export function buildContactResponseNameMap(contacts?: Contact[] | null): Record<string, string> {
    return (contacts ?? []).reduce(
        (map, contact) => {
            map[contact.contactId] = formatContactName(
                contact.firstName,
                contact.middleName,
                contact.lastName,
            );
            return map;
        },
        {} as Record<string, string>,
    );
}

export function buildContactEmailNameMap(
    userContactDetails: ContactResponse[],
    contactResponses?: Contact[] | null,
): Record<string, string> {
    const map: Record<string, string> = {};

    for (const contact of userContactDetails) {
        const email = normalizeEmail(contact.email);
        const name = formatContactName(contact.firstName, contact.middleName, contact.lastName);
        if (email && name) {
            map[email] = name;
        }
    }

    for (const contact of contactResponses ?? []) {
        const email = normalizeEmail(contact.contactEmail);
        const name = formatContactName(contact.firstName, contact.middleName, contact.lastName);
        if (email && name) {
            map[email] = name;
        }
    }

    return map;
}

function formatStoredRequestName(
    firstName?: string | null,
    lastName?: string | null,
): string | null {
    const name = formatContactName(firstName, null, lastName);
    return name || null;
}

export function resolveIdVerificationUserName(
    request: Pick<
        VerificationRequestResponse,
        'requesterClientRequestId' | 'email' | 'firstName' | 'lastName'
    >,
    contactMap: Record<string, string>,
    emailContactMap: Record<string, string>,
): string {
    const storedName = formatStoredRequestName(request.firstName, request.lastName);
    if (storedName) {
        return storedName;
    }

    if (request.requesterClientRequestId && contactMap[request.requesterClientRequestId]) {
        return contactMap[request.requesterClientRequestId];
    }

    const email = normalizeEmail(request.email);
    if (email && emailContactMap[email]) {
        return emailContactMap[email];
    }

    return 'Unknown';
}
